import { Box, Stack, styled, Theme, Typography } from '@mui/material';
import Decimal from 'decimal.js';
import { first, isEmpty, map } from 'lodash';
import { ReactNode, useEffect, useState } from 'react';

import { OversizedMetricItem } from '@/components/display/OversizedMetricItem/OversizedMetricItem';
import { ButtonGroup } from '@/components/form/baseInputs/ButtonGroup';
import { Card, CardProps } from '@/components/layout/Card/Card';
import { Badge, BadgeVariants } from '@/components/notifications/Badge/Badge';
import { InfoTooltip } from '@/components/poppers/Tooltip/InfoTooltip';
import { Loader } from '@/components/progress/Loader/Loader';
import { AssetIntegrationProviders } from '@/modules/assetProviderIntegrations/shared/constants';
import { ContextualHelpTooltip } from '@/modules/content/components/ContextualHelpTooltip';
import { COLORS } from '@/styles/tokens/colors';
import { WorkflowTrackerStatus } from '@/types/schema';
import { formatCurrencyNoDecimals } from '@/utils/formatting/currency';

import { EntityDetail_IntegrationEntityFragment } from '../../graphql/EntityDetailPage.generated';
import { InitialIntegrationSyncError } from '../../shared/InitialIntegrationSyncError';
import { EntitySyncBadge } from './EntitySyncBadge';
import { EntityValuationBreakdown } from './EntityValuationBreakdown';
import { EntityValuationItemProps } from './EntityValuationItem';
import { EntityValuationSumLine } from './EntityValuationSumLine';

interface AssetPropertyGroup {
  /**
   * If two different groups names are passed, the asset property
   * items will be broken up into two two different groups below a buttonGroup
   */
  groupName: string | null;
  items: EntityValuationItemProps[];
}

export interface OwnedEntityWithDiscount {
  displayName: string;
  currentDiscountedValue: Decimal;
}

export interface IntegrationDetails {
  integrationEntities: EntityDetail_IntegrationEntityFragment[];
  syncStatus: WorkflowTrackerStatus;
  lastSyncedAt: Date | null;
  provider: AssetIntegrationProviders;
}

/**
 * EntityValuationVariant describes the styling pattern of the EntityValuationCard.
 * card - renders with a border and padding
 * plain - renders with no border or external padding
 */
export type EntityValuationVariant = 'card' | 'plain';

export interface EntityValuationCardProps {
  entityName?: string;
  headlineValueLabel: React.ReactNode;
  headlineValue: React.ReactNode;
  headlineTooltip?: React.ReactNode;
  currentDiscountedValue?: Decimal;
  effectiveDiscountPercent?: Decimal;
  ownedEntitiesWithDiscounts?: OwnedEntityWithDiscount[];
  notes?: React.ReactNode;
  integrationDetails: IntegrationDetails | null;
  /** If present, sumLineValue will render as a tally line below the breakdown of the assets */
  sumLineValue?: Decimal;
  hasValidValuation: boolean;
  assetPropertyGroups: AssetPropertyGroup[];
  variant: EntityValuationVariant;
  actions: React.ReactNode;
  dateOfValuation?: Date;
}

export const HeaderWrapper = styled('div')(({ theme }: { theme: Theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  gap: theme.spacing(1),
  justifyContent: 'space-between',
  width: '100%',
  alignItems: 'center',
  // @container selections are not native to MUI yet, but will be soon.
  // See: https://github.com/mui/material-ui/issues/25189#issuecomment-1321236185
  [theme.breakpoints.down(280).replace('@media', '@container')]: {
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
}));

const HORIZONTAL_PADDING = 1.5;

const cardPropsForVariant: Record<EntityValuationVariant, CardProps> = {
  card: { variant: 'outlined', sx: { p: 2 } },
  plain: { variant: 'transparent', sx: {} },
};

function DiscountedValueBadge({
  currentDiscountedValue,
  effectiveDiscountPercent,
  ownedEntitiesWithDiscounts,
  entityName,
}: Pick<
  EntityValuationCardProps,
  | 'currentDiscountedValue'
  | 'effectiveDiscountPercent'
  | 'ownedEntitiesWithDiscounts'
  | 'entityName'
>) {
  if (!currentDiscountedValue || currentDiscountedValue.eq(0)) return null;
  if (!effectiveDiscountPercent || effectiveDiscountPercent.eq(0)) return null;

  const body = (
    <>{formatCurrencyNoDecimals(currentDiscountedValue)} discounted value</>
  );

  let infoTooltipTitle: ReactNode | undefined;
  if (!isEmpty(ownedEntitiesWithDiscounts)) {
    const firstOwnedEntityWithDiscount = first(ownedEntitiesWithDiscounts);
    if (
      ownedEntitiesWithDiscounts?.length === 1 &&
      firstOwnedEntityWithDiscount
    ) {
      infoTooltipTitle = `The discounted value of ${entityName} accounts for the discounted value of ${firstOwnedEntityWithDiscount.displayName} of ${formatCurrencyNoDecimals(firstOwnedEntityWithDiscount.currentDiscountedValue)}.`;
    } else {
      infoTooltipTitle = `The discounted value of ${entityName} accounts for ownership in the following discounted entities: ${ownedEntitiesWithDiscounts?.map((share) => share.displayName).join(', ')}.`;
    }
  }

  if (infoTooltipTitle) {
    return (
      <Badge
        wrapperSx={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'nowrap',
        }}
        display={
          <Stack direction="row" alignItems="center" gap={0.5}>
            {body}
            <InfoTooltip title={infoTooltipTitle} />
          </Stack>
        }
        variant={BadgeVariants.Accent}
      />
    );
  }

  return <Badge display={body} variant={BadgeVariants.Accent} />;
}

/**
 * @description EntityValuationCard is a presentational component that displays a breakdown of the
 * entity's valuation and exposes a set of possible actions to take to update the valuation
 */
export function EntityValuationCard({
  headlineValue,
  headlineValueLabel,
  headlineTooltip,
  currentDiscountedValue,
  effectiveDiscountPercent,
  ownedEntitiesWithDiscounts,
  entityName,
  actions,
  variant,
  ...valuationContentProps
}: EntityValuationCardProps) {
  const { integrationDetails } = valuationContentProps;
  const { sx, ...cardProps } = cardPropsForVariant[variant];

  return (
    <Card {...cardProps} sx={{ height: '100%', ...sx }}>
      <Stack spacing={2} sx={{ containerType: 'inline-size' }}>
        <Stack spacing={0.5}>
          <HeaderWrapper data-name="HeaderWrapper">
            <OversizedMetricItem
              titleVariant="title-s"
              valueVariant="title-xl"
              valueColor={COLORS.TEXT.PRIMARY}
              tooltip={
                headlineTooltip && (
                  // need inline-block here to make sure that the tooltip is rendered
                  // inline with the headline
                  <ContextualHelpTooltip sx={{ display: 'inline-block' }}>
                    {headlineTooltip}
                  </ContextualHelpTooltip>
                )
              }
              title={headlineValueLabel}
              value={headlineValue}
            />
            {integrationDetails && (
              <EntitySyncBadge
                sx={{ flexShrink: 0 }}
                lastSyncStatus={integrationDetails.syncStatus}
                lastSyncedAt={integrationDetails.lastSyncedAt}
              />
            )}
          </HeaderWrapper>
          <DiscountedValueBadge
            currentDiscountedValue={currentDiscountedValue}
            effectiveDiscountPercent={effectiveDiscountPercent}
            ownedEntitiesWithDiscounts={ownedEntitiesWithDiscounts}
            entityName={entityName}
          />
        </Stack>
        <ValuationOrIntegrationContent {...valuationContentProps} />
        <Box mt={2}>{actions}</Box>
      </Stack>
    </Card>
  );
}

interface ValuationOrIntegrationContentProps extends ValuationContentProps {
  integrationDetails: IntegrationDetails | null;
  hasValidValuation: boolean;
}

function ValuationOrIntegrationContent({
  integrationDetails,
  hasValidValuation,
  ...props
}: ValuationOrIntegrationContentProps) {
  // if there's a sync workflow in progress, always show the loader
  if (integrationDetails?.syncStatus === WorkflowTrackerStatus.Started) {
    return (
      <Loader
        boxProps={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 2,
        }}
        circularProgressProps={{
          size: 80,
        }}
      />
    );
  }

  // we only want to show the error state in this way if the initial sync workflow is
  // failed and we have nothing to fall back on, otherwise we'd want to show the normal valuation content
  // and rely on the integration badge to show a more subtle badge to indicate that just the most recent
  // sync failed
  if (
    integrationDetails?.syncStatus === WorkflowTrackerStatus.Failed &&
    !hasValidValuation
  ) {
    return (
      <InitialIntegrationSyncError provider={integrationDetails.provider} />
    );
  }

  return <ValuationContent {...props} />;
}

interface ValuationContentProps {
  assetPropertyGroups: AssetPropertyGroup[];
  sumLineValue?: Decimal;
  notes?: React.ReactNode;
}

function ValuationContent({
  assetPropertyGroups,
  sumLineValue,
  notes,
}: ValuationContentProps) {
  const [entityBreakdownOption, setEntityBreakdownOption] = useState<
    string | null
  >(null);

  useEffect(
    function autoSelectFirstBreakdownOption() {
      // make sure that if we already have a selected group, if assetPropertyGroups changes and that
      // group is still present, we keep it selected. this resolves a bug where when assetPropertyGroups
      // recomputed because of a new valuation, it would always reset the selected group to the first one
      const currentSelectedGroup = assetPropertyGroups.find(
        (g) => g.groupName === entityBreakdownOption
      );
      const selectedGroup =
        currentSelectedGroup?.groupName ??
        assetPropertyGroups[0]?.groupName ??
        null;
      setEntityBreakdownOption(selectedGroup);
    },
    [assetPropertyGroups, entityBreakdownOption]
  );

  const shouldFilterPropertyGroups = assetPropertyGroups.length > 1;
  return (
    <>
      {shouldFilterPropertyGroups && (
        <ButtonGroup
          options={assetPropertyGroups.map(({ groupName }) => ({
            display: groupName ?? 'Unknown',
            value: groupName ?? 'Unknown',
          }))}
          value={entityBreakdownOption}
          variant="light"
          label="Entity breakdown options"
          hideLabel
          onChange={(_e, v) => setEntityBreakdownOption(v)}
        />
      )}
      {map(assetPropertyGroups, ({ items, groupName }) => {
        if (shouldFilterPropertyGroups && entityBreakdownOption !== groupName) {
          return null;
        }

        return (
          <Box key={groupName}>
            <EntityValuationBreakdown valuationItems={items} />
          </Box>
        );
      })}
      {sumLineValue && (
        <EntityValuationSumLine
          value={sumLineValue}
          padding={HORIZONTAL_PADDING}
        />
      )}
      <Typography variant="body-s">{notes}</Typography>
    </>
  );
}
